/**
 * Storage Validation
 *
 * Pure validation functions for data before storage.
 * These are storage-agnostic and can be used by any storage provider
 * (local SQL, remote API, etc.)
 */

import type { BudgetCreateData, RuleCreateData } from '../types';

/**
 * Validation error type
 */
export type ValidationError = {
  field: string;
  message: string;
};

/**
 * Validation result type
 */
export type ValidationResult = {
  valid: boolean;
  errors: ValidationError[];
};

/**
 * Validate budget data before creation
 *
 * @param data - Budget creation data
 * @returns Validation result with any errors
 */
export function validateBudget(data: BudgetCreateData): ValidationResult {
  const errors: ValidationError[] = [];

  // Name validation
  if (!data.name?.trim()) {
    errors.push({
      field: 'name',
      message: 'Budget name is required'
    });
  }

  // Categories validation
  if (!data.categories || data.categories.length === 0) {
    errors.push({
      field: 'categories',
      message: 'At least one category is required'
    });
  }

  // Type validation
  if (!data.type || !['monthly', 'quarterly', 'yearly'].includes(data.type)) {
    errors.push({
      field: 'type',
      message: 'Invalid budget type (must be: monthly, quarterly, or yearly)'
    });
  }

  // Limit validation
  if (data.limit <= 0) {
    errors.push({
      field: 'limit',
      message: 'Budget limit must be greater than 0'
    });
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validate rule data before creation
 *
 * @param pattern - Rule pattern
 * @param category - Category to assign
 * @returns Validation result with any errors
 */
export function validateRule(pattern: string, category: string): ValidationResult {
  const errors: ValidationError[] = [];

  // Pattern validation
  if (!pattern) {
    errors.push({
      field: 'pattern',
      message: 'Pattern is required'
    });
  }

  // Category validation
  if (!category) {
    errors.push({
      field: 'category',
      message: 'Category is required'
    });
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validate category name
 *
 * @param name - Category name
 * @returns Validation result with any errors
 */
export function validateCategoryName(name: string): ValidationResult {
  const errors: ValidationError[] = [];

  if (!name?.trim()) {
    errors.push({
      field: 'name',
      message: 'Category name is required'
    });
  }

  if (name && name.length > 100) {
    errors.push({
      field: 'name',
      message: 'Category name must be 100 characters or less'
    });
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validate transaction amount
 *
 * @param amount - Transaction amount
 * @returns Validation result with any errors
 */
export function validateAmount(amount: number): ValidationResult {
  const errors: ValidationError[] = [];

  if (isNaN(amount)) {
    errors.push({
      field: 'amount',
      message: 'Amount must be a valid number'
    });
  }

  if (amount === 0) {
    errors.push({
      field: 'amount',
      message: 'Amount cannot be zero'
    });
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validate date string
 *
 * @param date - Date string
 * @returns Validation result with any errors
 */
export function validateDate(date: string): ValidationResult {
  const errors: ValidationError[] = [];

  if (!date?.trim()) {
    errors.push({
      field: 'date',
      message: 'Date is required'
    });
    return { valid: false, errors };
  }

  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) {
    errors.push({
      field: 'date',
      message: 'Invalid date format'
    });
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Helper to get first error message
 *
 * @param result - Validation result
 * @returns First error message or undefined
 */
export function getFirstError(result: ValidationResult): string | undefined {
  return result.errors[0]?.message;
}

/**
 * Helper to combine multiple validation results
 *
 * @param results - Array of validation results
 * @returns Combined validation result
 */
export function combineValidationResults(...results: ValidationResult[]): ValidationResult {
  const allErrors = results.flatMap(r => r.errors);
  return {
    valid: allErrors.length === 0,
    errors: allErrors
  };
}
